















import { bus } from "@/bus";
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { Getter } from "vuex-class";
import { fixedHtmlData, FixedHtmlProperties, fileUploadStages, fixedHtmlCSS, DataVsIdDuration } from "@/services/posts/postTypes/fixedhtml";
import { getContentTemplateWithoutAclCheck } from "@/services/contentTemplates/contentTemplateService";
import { getVueTemplateAndData } from "@/services/posts/postsService";
import { RuntimeTemplateCompiler } from "vue-runtime-template-compiler";
import FileSelectModal from "@/components/files/modals/FileSelectModal.vue";
import DirectFileUpload from "@/components/common/files/DirectFileUpload.vue";
import { MediaType } from "@/services/files/types";
import { FILEENDPOINT } from "@/app.config";
import { FolderEntites } from "@/services/folders/types";

// Components
import MediaComponentForm from "./formComponents/MediaComponentForm.vue";
import TextComponentForm from "./formComponents/TextComponentForm.vue";
import FormatTextComponentForm from "./formComponents/FormatTextComponentForm.vue";
import MacrosComponentForm from "./formComponents/MacrosComponentForm.vue";
import { ChannelStyle, Style } from "@/services/styles/types";
import { getChannelStyle } from "@/services/channels/channelsService";
import QrcodeComponentForm from "./formComponents/QrcodeComponentForm.vue";
import SelectStockImageModal from "@/components/files/modals/SelectStockImageModal.vue";

@Component({
    components: {
        mediacomponentform: MediaComponentForm,
        textcomponentform: TextComponentForm,
        formattextcomponentform: FormatTextComponentForm,
        macroscomponentform: MacrosComponentForm,
        qrcodecomponentform: QrcodeComponentForm,
        RuntimeTemplateCompiler,
        FileSelectModal,
        DirectFileUpload,
        SelectStockImageModal
    }
})
export default class FixedHtmlForm extends Vue {
    @Getter("getAllowStockImages") allowStockImages!: boolean;
    // Props from _FixedHtmlTemplateForm used for the preview when you are creating a Template
    @Prop() itemStyle!: Style; // Styles defined on PostStyle
    @Prop() templateSourceCode!: string; // Template Code

    // Props from _PostForm, used for styles when you are Creating a Post,
    @Prop() private channelId!: string; // Used to get the fixedhtml styles attached to respective channel.
    @Prop() private contentTemplateId!: string;

    // Props used in both scenarios ( creating a template or creating a post)
    @Prop() private properties!: FixedHtmlProperties;
    @Prop() channelHeight!: number;
    @Prop() channelWidth!: number;

    localProperties = {} as FixedHtmlProperties;
    update = false;
    mediaTypes = [MediaType.Image];
    currentMediaId = "";
    folders = FolderEntites;
    fileuploadinprogress = { fileIsUploading: false, mediaId: "", failedUploading: false } as fileUploadStages;
    templateToParse: string = "";
    channelStyleInfo: ChannelStyle | {} = {};
    stylesForPost = "";
    dataVsIdDurations = [] as DataVsIdDuration[];

    // Watcher
    @Watch("templateSourceCode") templateReAssignment() {
        this.templateToParse = this.templateSourceCode;
    }

    // when we are creating / editing a post we bring the styles applied for this channel and store them in a locally
    created() {
        if (this.channelId) {
            getChannelStyle(this.channelId, "fixedhtml").then(response => {
                this.channelStyleInfo = response;
                // If the channel has styles applied, we store them in a local variable to be used in the post form.
                if (this.channelStyleInfo && (this.channelStyleInfo as ChannelStyle).css) {
                    this.stylesForPost = (this.channelStyleInfo as ChannelStyle).css.wrapper;
                }
            });
        }
    }

    mounted() {
        // when we are creating / editing a template we want the changes to be local to the browser
        // when we are creating / editing a post we are fetching the template via its Id from backend

        if (this.properties) {
            this.localProperties = this.properties;
        } else {
            this.$emit("setDefaultValues", JSON.stringify(this.localProperties));
        }

        if (!this.localProperties.contentTemplateId) {
            this.localProperties.contentTemplateId = this.contentTemplateId;
        }

        if (this.localProperties.contentTemplateId) {
            getContentTemplateWithoutAclCheck(this.localProperties.contentTemplateId).then(response => {
                this.templateToParse = response.content.html;
            });
        } else {
            this.templateToParse = this.templateSourceCode;
        }

        // Passing props to file select model because we are passing forward to the file manager but for the direct upload and drag and drop we are using parameter.
        bus.$on("openFileSelect", (id: string, mediaTypes: MediaType[]) => {
            this.currentMediaId = id;
            // Update Props for openFileSelectModal
            this.mediaTypes = mediaTypes;
            (this.$refs.fileSelectModal as any).open();
        });

        bus.$on("openDirectUpload", (id: string, mediaTypes: MediaType[]) => {
            this.currentMediaId = id;
            (this.$refs.directUpload as any).openFileUpload(mediaTypes);
        });

        bus.$on("stockImageDownload", (id: string) => {
            this.currentMediaId = id;
            (this.$refs.selectStockImageModal as any).open();
        });

        bus.$on("injectFile", (file: any, id: string, mediaTypes: MediaType[]) => {
            this.currentMediaId = id;
            (this.$refs.directUpload as any).injectFile(file, mediaTypes);
        });

        // Set the post form duration based on the longest available video duration.
        // For Image media type, the duration is set to 0, enabling the display of an editable duration field on the post if the longest duration is 0.
        bus.$on("setDuration", (duration: number, id: string) => {
            var dataVsIdDurationIndex = this.dataVsIdDurations.findIndex(dataTypeDuration => dataTypeDuration.id === id);
            this.dataVsIdDurations[dataVsIdDurationIndex].duration = duration;

            // Using the reduce function to find the maximum duration
            const longestDuration = this.dataVsIdDurations.reduce((maxDuration, dataVsIdDuration) => {
                return Math.max(maxDuration, dataVsIdDuration.duration);
            }, this.dataVsIdDurations[0].duration);

            // send longest duration value to the post form.
            this.$emit("setDuration", longestDuration);
        });
    }

    destroyed() {
        bus.$off("openFileSelect");
        bus.$off("openDirectUpload");
        bus.$off("injectFile");
        bus.$off("setDuration");
    }

    get channelSize() {
        return `width: ${this.channelWidth}px;
                height: ${this.channelHeight}px;`;
    }

    clearDataOnPreview() {
        this.localProperties.data = [];
    }

    onSelect(file: any) {
        bus.$emit("changeMedia", this.currentMediaId, FILEENDPOINT + file.fileId, file.mediaType);
        this.$bvModal.hide("modal-fileSelect");
        this.fileuploadinprogress.fileIsUploading = false;
        this.fileuploadinprogress.mediaId = "";
    }

    onStockFileUploaded(file: any) {
        bus.$emit("changeMedia", this.currentMediaId, FILEENDPOINT + file.fileId, file.mediaType);
        this.$bvModal.hide("modal-stockImages");
    }

    emitData() {
        bus.$emit("getComponentData"); // Get data from each individual component and update the main template object
        this.$emit("propertiesUpdated", this.localProperties);
    }

    get styleModel(): string {
        let style = "";
        if (this.itemStyle && this.itemStyle.css as fixedHtmlCSS) {
            const css = this.itemStyle.css as fixedHtmlCSS;
            style = css.wrapper || "";
        } else {
            style = this.stylesForPost;
        }

        if (this.channelWidth || this.channelHeight) {
            style += ` width: ${this.channelWidth}px; height: ${this.channelHeight}px; --channel-width: ${this.channelWidth}px; --channel-height: ${this.channelHeight}px;`;
        }

        return style;
    }

    get templateToRender():string {
        bus.$emit("getComponentData");
        var templateAndData = getVueTemplateAndData(this.templateToParse);
        const { template, data } = templateAndData;
        const keysThatContainData = ["id", "text", "src", "behavior", "qrCodeValue", "macros"];
        // Match the data without altering fields that might contain user-data
        if (!this.localProperties.data) {
            this.localProperties.data = data;
        } else {
            for (const i in data) {
                const element: fixedHtmlData = data[i];
                const localElement: fixedHtmlData = this.localProperties.data.filter(x => x.id === element.id)[0];

                if (!localElement) this.localProperties.data.push(element);
                else {
                    Object.keys(element).forEach(key => {
                        if (!keysThatContainData.includes(key)) {
                            localElement[key] = element[key];
                        }
                    });
                }
            }
        }
        // Store DataVsId with durations to handle longest duration from the media component videos.
        this.dataVsIdDurations = [] as DataVsIdDuration[]; // empty the array before storing.
        data.forEach(element => {
            var dataTypeDuration = {} as DataVsIdDuration;
            dataTypeDuration.id = element.id;
            dataTypeDuration.duration = 0; // by default set it to 0
            this.dataVsIdDurations.push(dataTypeDuration);
        });
        this.update = !this.update;
        return template;
    }

    fileUploadFailed() {
        this.fileuploadinprogress.fileIsUploading = false;
        this.fileuploadinprogress.failedUploading = true;
        setTimeout(() => {
            this.fileuploadinprogress.failedUploading = false;
            this.fileuploadinprogress.mediaId = "";
        }, 2000);
    }

    uploadStarted() {
        this.fileuploadinprogress.mediaId = this.currentMediaId;
        this.fileuploadinprogress.fileIsUploading = true;
    }
}
